import React from 'react';
import { makePhase, normalizePhases } from '../../lib/database';
import PhaseCalendarStrip from './PhaseCalendarStrip';

/**
 * PhasePlannerInline — the CONTROLLED entry point for the phase planner on
 * the ClientPricingTab (where the estimate doesn't exist yet at render time).
 * It's a thin wrapper around <PhaseCalendarStrip> (now a sequence-pipeline
 * view — no calendar) that forwards the phases array + onChange callback.
 *
 * Props:
 *   phases    — array of planned-phase objects (shape from makePhase)
 *   onChange  — (nextPhases) => void, called with a freshly normalized array
 *   disabled  — optional, hides all edit controls
 *   moveDate  — kept for API compat, not used (dates are set on dispatch board)
 */
export default function PhasePlannerInline({ phases = [], onChange, disabled = false, moveDate = null }) {
    return (
        <PhaseCalendarStrip
            phases={phases}
            onChange={onChange}
            disabled={disabled}
        />
    );
}

// ============================================================================
// derivePhases — build the initial planned_phases array from the pricing
// state of the ClientPricingTab. This is the single source of the "Smart
// defaults" auto-seed rules:
//
//   LOCAL + no packing → [move]                              (1 phase)
//   LOCAL + packing    → [pack, move]                        (2 phases)
//   BOL   + no packing → [load, drive, unload]               (3 phases)
//   BOL   + packing    → [pack, load, drive, unload]         (4 phases)
//
// ALL phases are dateless — the planner defines only the trip shape
// (sequence, kind, crew size, duration). Actual calendar dates are
// assigned on the dispatch board when the trip is dragged onto a crew row.
//
// Crew sizes inherit from the correct pricing input for each phase kind:
//    pack                → packingCrewSize
//    LOCAL move          → crewSize
//    BOL load            → loadCrewSize
//    BOL drive / unload  → travelCrewSize
// ============================================================================
export function derivePhases({
    jobType = 'LOCAL',
    packingNeeded = 'none',
    crewSize = 0,
    loadCrewSize = 0,
    travelCrewSize = 0,
    packingCrewSize = 0,
    truckType = '',
    bolTruckType = '',
    moveDate = null, // kept for API compat, not assigned to phases
    packDate = null, // optional packing day — stamped on the pack phase so it
    packTime = null, // lands on its own day on the board (usually move − 1)
    // Day-1 packing hours/rate — stamped onto the pack phase so each packing
    // day carries its OWN estimate to the board (multi-day packing, Jensen
    // 2026-08-25). Zero/blank = not individually priced (legacy single-day
    // behavior, est.packing_hours still rules).
    packingMinHours = 0,
    packingMaxHours = 0,
    packingRate = 0,
    // Additional packing days beyond day 1: array of
    // { date, time, crew, rate, minHours, maxHours } — one PACK phase each.
    extraPackDays = [],
} = {}) {
    const isBOL = jobType === 'LONG_DISTANCE';
    const hasPacking = packingNeeded && packingNeeded !== 'none' && packingNeeded !== 'tbd';
    const out = [];
    let seq = 1;

    if (hasPacking) {
        out.push({
            ...makePhase({
                kind: 'pack',
                sequence: seq++,
                crew_size: Number(packingCrewSize) || 0,
                trucks_needed: '',
                date: packDate || null,
                time: packTime || null,
            }),
            est_min_hours: parseFloat(packingMinHours) || 0,
            est_max_hours: parseFloat(packingMaxHours) || parseFloat(packingMinHours) || 0,
            hourly_rate: parseFloat(packingRate) || 0,
        });
        for (const day of (Array.isArray(extraPackDays) ? extraPackDays : [])) {
            out.push({
                ...makePhase({
                    kind: 'pack',
                    sequence: seq++,
                    crew_size: Number(day.crew) || 0,
                    trucks_needed: '',
                    date: day.date || null,
                    time: day.time || null,
                }),
                est_min_hours: parseFloat(day.minHours) || 0,
                est_max_hours: parseFloat(day.maxHours) || parseFloat(day.minHours) || 0,
                hourly_rate: parseFloat(day.rate) || 0,
            });
        }
    }

    if (isBOL) {
        out.push(makePhase({
            kind: 'load',
            sequence: seq++,
            crew_size: Number(loadCrewSize) || 0,
            trucks_needed: bolTruckType || '',
        }));
        out.push(makePhase({
            kind: 'drive',
            sequence: seq++,
            crew_size: Number(travelCrewSize) || 0,
            trucks_needed: bolTruckType || '',
        }));
        out.push(makePhase({
            kind: 'unload',
            sequence: seq++,
            crew_size: Number(travelCrewSize) || 0,
            trucks_needed: bolTruckType || '',
        }));
        // RETURN — the travel crew drives back; same crew size as DRIVE/UNLOAD
        out.push(makePhase({
            kind: 'return',
            sequence: seq++,
            crew_size: Number(travelCrewSize) || 0,
            trucks_needed: bolTruckType || '',
        }));
    } else {
        out.push(makePhase({
            kind: 'move',
            sequence: seq++,
            crew_size: Number(crewSize) || 0,
            trucks_needed: truckType || '',
        }));
    }

    return normalizePhases(out);
}
